import {
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  forwardRef,
} from "@chakra-ui/react";

/**
 * Input — text input matching `.input` in the redesign bundle.
 * design ref: styles.css:833–872
 *
 * Wraps Chakra `Input` but applies our exact tokens. We bypass Chakra's
 * built-in `size` (which controls padding+font height) and provide our own
 * `sm` / `md` (default) that match `.input--sm` vs `.input`.
 */
export interface InputProps extends Omit<ChakraInputProps, "size"> {
  size?: "sm" | "md";
}

const SIZE_STYLES = {
  md: { px: "md", py: "sm", fontSize: "md" }, // 8px 12px / 14px
  sm: { px: "2.5", py: "1xs", fontSize: "smd" }, // 6px 10px / 13px
} as const;

const Input = forwardRef<InputProps, "input">(
  ({ size = "md", ...rest }, ref) => {
    const s = SIZE_STYLES[size];
    return (
      <ChakraInput
        ref={ref}
        // Chakra defaults to its own variant; override everything to match `.input`.
        variant="unstyled"
        width="100%"
        display="block"
        px={s.px}
        py={s.py}
        fontSize={s.fontSize}
        fontFamily="inherit"
        color="neutral.950"
        lineHeight="1.4"
        bg="neutral.25"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="neutral.300"
        borderRadius="component.lg"
        boxShadow="xs"
        transition="border-color 120ms, box-shadow 120ms, background-color 120ms"
        _placeholder={{ color: "neutral.500" }}
        _hover={{
          borderColor: "neutral.400",
          bg: "neutral.0",
        }}
        _focus={{
          borderColor: "p.500",
          boxShadow: "focusRing",
          outline: "none",
        }}
        _focusVisible={{
          borderColor: "p.500",
          boxShadow: "focusRing",
          outline: "none",
        }}
        _disabled={{
          bg: "neutral.100",
          color: "neutral.500",
          borderColor: "neutral.200",
          cursor: "not-allowed",
          _hover: {
            bg: "neutral.100",
            borderColor: "neutral.200",
          },
        }}
        {...rest}
      />
    );
  },
);

export default Input;
